import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import ChartContainer from "./ChartContainer";
import { useTheme } from "../../hooks/useTheme";
import { getChartColors } from "../../utils/chartColors";

interface BarChartData {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  data: BarChartData[];
  title?: string;
  showValues?: boolean;
  className?: string;
  height?: string;
}

const BarChartComponent: React.FC<BarChartProps> = ({
  data,
  title = "Bar Chart",
  showValues = true,
  className = "",
  height = "h-64",
}) => {
  const { isDark } = useTheme();
  const colors = getChartColors(isDark);

  if (data.length === 0) {
    return (
      <ChartContainer title={title} className={className} height={height}>
        <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400">
          No data available
        </div>
      </ChartContainer>
    );
  }

  const chartData = data.map((item) => ({
    name: item.label,
    value: item.value,
    fill: item.color || colors.primary,
  }));

  return (
    <ChartContainer title={title} className={className} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
          <XAxis
            dataKey="name"
            type="category"
            stroke={colors.text}
            tick={{ fill: colors.text, fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={40}
          />
          <YAxis
            type="number"
            stroke={colors.text}
            tick={{ fill: colors.text }}
          />
          <Tooltip
            formatter={(value: number) => [value, "Count"]}
            labelFormatter={(label) => label}
            contentStyle={{
              backgroundColor: colors.tooltip.background,
              border: `1px solid ${colors.tooltip.border}`,
              borderRadius: "8px",
              color: colors.tooltip.text,
            }}
          />
          <Bar dataKey="value" fill={colors.primary} />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default BarChartComponent;
